/**
 * Subsystem registry — the ONE place render subsystems are named and ordered.
 *
 * The five M7 subsystems (pitch, players, lighting, stadium, VFX, post) are each
 * built by a separate agent in its own file. They never import each other and
 * never touch renderer.ts. This file is the only thing that knows the full set,
 * so wiring a subsystem in is a one-line change here and nothing collides.
 *
 * ORDER MATTERS for two reasons:
 *   1. Lighting must init before materials that read its IBL/shadow uniforms.
 *   2. The composite (post) subsystem must be LAST — it wraps everyone's output.
 *      The shell finds it via isCompositeSubsystem regardless of position, but
 *      keeping it last keeps the mental model honest.
 *
 * MIGRATION STATE: while the real subsystems are being written, this returns the
 * placeholder (the ported M1–M6 capsule/pitch look) so the client always runs.
 * As each real subsystem lands and is verified, swap it in here and drop the
 * placeholder's coverage of that domain. When all five are in, remove the
 * placeholder import entirely.
 */

import type { RenderContext, RenderSubsystem } from './context.js';

/**
 * Master switch for the anime-stadium bowl at the flagship mount point.
 *
 * UN-ARCHIVED 2026-08-12 (owner directive: "keep these stadiums on the game").
 * This reverses the 2026-07-23 "must ship false" directive, which was written
 * against a very different asset: the old V5.1 handoff was 82 MB, its crowd was
 * 85k skinned people at 59.6M triangles, and it never had a pitch that lined up
 * with the sim. The anime export that replaced it is 142k triangles all in, the
 * crowd is 31,635 two-triangle billboards in a single draw call, and its pitch
 * and goals land exactly on the sim's own dimensions (see V5_MOUNT_KEYS and
 * V5_BOWL_YAW). The reasons for archiving it no longer hold.
 *
 * `false` still selects the procedural bowl (render/stadium.ts), which stays on
 * disk and working — flipping this back is the whole rollback.
 *
 * NOTE the two knock-on switches below: with V5 active the procedural PITCH
 * SURFACE and GOAL FRAMES must stay off, or they z-fight with the modelled ones.
 */
export const FLAGSHIP_STADIUM_V5 = true;

// ── Real M7 subsystems ──────────────────────────────────────────────────────
import { createLightingRig } from './lighting.js';
import { createPitchSystem } from './pitch.js';
import { createStadiumSystem } from './stadium.js';
import { createStadiumV5System } from './StadiumV5System.js';
// createSidelineSystem (sideline.ts) is NOT constructed below. Owner,
// 2026-08-14, after the coaching-staff/substitute/camera-crew figures turned
// out to be standing on the GRASS side of the touchline rather than the
// technical area they were designed for: "those are three.js models remove
// them we will add later the real models" — the figures are procedural
// low-poly placeholders (boxes + cylinders, sideline.ts's own header), not
// the ARIN character pipeline actual players use, and the plan is to replace
// them with real models rather than spend effort fixing a placeholder's
// position. Same retirement pattern as the Mixamo/debug player-visual
// systems just below: the file stays on disk (real engineering — tripod/
// photographer/manager positioning and its own cost-budget accounting) with
// nothing in the live subsystem list able to reach it, rather than deleted.
// THE CAST — the only player-visual system. Owner, 2026-08-14: "remove the
// prev char and keep only arin" — the Mixamo shared-master subsystem
// (player/playerVisual.ts) and the debug-capsule subsystem
// (createDebugPlayerVisual.ts) are no longer constructed here at all, so
// there is no runtime fallback left that can show the old character. Both
// files stay on disk (removing them cascades into renderer.ts's
// PlayerVisualHandle type and their own tests, a separate cleanup), but
// nothing in the live subsystem list can reach them any more.
import { createCastPlayerVisual } from './player/castPlayerVisual.js';
import { createBallSystem } from './ball.js';
import { createCornerTrailSystem } from './cornerTrail.js';
import { createIndicatorSystem } from './indicators.js';
import { createBoostAuraSystem } from './boostAura.js';
import { createBoostThunderSystem } from './boostThunder.js';
import { createVfxSystem } from './vfx.js';
import { createCelebrationVfx } from './celebration.js';
import { createCrowdAudioSystem } from './crowdAudio.js';
import { createPostPipeline } from './postfx.js';

export function buildSubsystems(_ctx: RenderContext): RenderSubsystem[] {
  // Ordering contract: lighting FIRST (it inits before materials that read its
  // IBL/shadow uniforms, and leaves tone mapping for post to own), post LAST
  // (the sole CompositeSubsystem — sets gl.toneMapping in init, wraps the frame).
  // PLAYERS: the cast (36 ARIN-pipeline characters, render/player/castPlayerVisual.ts)
  // draws every slot — see the import comment above for why nothing else can.
  // STADIUM: the OFFICIAL environment is the previous web-optimized procedural bowl
  // (createStadiumSystem — 180-FPS-oriented, visible goals/nets, lit, collidable).
  // runtime-handoff-v5.1 is ARCHIVED behind FLAGSHIP_STADIUM_V5 (default false) and
  // never loads in the shipped game — see the flag's note above.
  // When the V5 bowl is active it supplies the playing surface and the goal
  // frames itself (both modelled, both landing on the sim's own dimensions), so
  // the procedural equivalents are suppressed rather than drawn underneath —
  // two surfaces at y=0 z-fight across the whole field, and two goal frames
  // z-fight along every bar. The verlet goal NET is kept either way: it billows
  // off the ball, which the modelled alpha-panel net cannot do.
  const stadium = FLAGSHIP_STADIUM_V5 ? createStadiumV5System() : createStadiumSystem();
  return [
    createLightingRig(),
    createPitchSystem({ drawSurface: !FLAGSHIP_STADIUM_V5 }),
    stadium,
    createCastPlayerVisual({ enabled: true }),
    // ⚠ NOT MOUNTED, AND THAT IS THE CURRENT DECISION. `sideline.ts` builds
    // substitutes, coaches, warm-up subs and camera crews, and nothing ever
    // imported it. Mounting it (2026-08-15) put EIGHTEEN STANDING FIGURES
    // across 29 m of touchline — the owner photographed it twice — because its
    // anchors are a contract with `stadium.ts`'s bowl (its own header says so),
    // and the game ships the FLAGSHIP bowl, whose dugouts live somewhere else
    // entirely (markers.json: game z = -44 vs the -35.3 this file derives).
    // Moving the bench line onto the marker was not enough: the figures stand
    // rather than sit, and they span both dugouts AND the gap between them.
    //
    // Filling these dugouts properly means authoring the placement against the
    // flagship's actual bench geometry — a modelling job, not a constant — so
    // this stays unmounted until that is done, rather than shipping a row of
    // men standing on the grass. `createSidelineSystem` is intentionally
    // referenced nowhere; delete this comment when it is mounted for real.
    // createSidelineSystem(),
    // The visible match officials (render/referee.ts) went with the character
    // reset: they were animated by the same GLTF pipeline. Referee LOGIC and
    // the RefereeSignal wire frame are untouched — only the drawn official is
    // gone until there is a character system to draw one with.
    // ball.ts exists because retiring the placeholder orphaned the ball mesh —
    // vfx only ever drew the trail. See ball.ts header before "simplifying".
    createBallSystem(),
    // indicators = controlled-player ring + pass-target disc (§4.8/§4.2, 3C).
    // The ring seam existed since M7 but only the retired players.ts drew it —
    // the gltf system never did, which is playtest defect #14.
    createIndicatorSystem(),
    // The stamina-boost aura (owner: "add a red aura on player when he
    // activates it"). Its own subsystem, and NOT an attachment inside
    // castPlayerVisual.ts, because anything hung off a character there hangs off
    // a LOD TIER and vanishes on the next distance swap — see boostAura.ts's
    // header. Position-fed, so it also works whichever player subsystem above
    // is mounted. Before post, so bloom picks up the glow.
    createBoostAuraSystem(),
    // Tier-2-only lightning arcing between the same five body anchors the
    // embers use (owner: "add thunders on player when he is on boost 2").
    // Its own subsystem for the same reason boostAura.ts is one — position-
    // fed, not attached to a character LOD tier — and placed right after it
    // so the two read as one layered effect, both landing before post.
    createBoostThunderSystem(),
    // The corner delivery arc + landing ring (owner: "like FIFA game"). Its own
    // subsystem rather than a third marker inside indicators.ts, which is pinned
    // at zero polylines on purpose — see cornerTrail.ts's header.
    createCornerTrailSystem(),
    createVfxSystem({ drawGoalFrames: !FLAGSHIP_STADIUM_V5 }),
    // Goal celebration: confetti over the bowl, plus fireworks after dark.
    // Sits before post so the sparks pick up bloom.
    createCelebrationVfx(),
    // Stadium ambience (reverbed) + a per-stand chant that only carries when
    // the controlled player is near that stand (owner: "use this audio... and
    // give a reverb", "use the audio i marked at some points in the stadium
    // they can be only heard when you go close to those audience"). Audio has
    // no draw order — position here is just "reads as atmosphere, near the
    // other atmosphere systems", not a rendering dependency.
    createCrowdAudioSystem(),
    createPostPipeline(),
  ];
}
